import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { axios, APIBASE_REVIVERUSERS } from "./ApiConfig";
import {
  AddressType,
  AddressTypes,
  CompanyType,
  Country,
  DropdownOption,
  Language,
  PartialCompanyRegistration,
  Region,
} from "@/types";
import { AxiosResponse } from "axios";
import { RegisterCompanyFormInput } from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";

export const startPartialCompany = async (userId: any) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/registration/start`, {
    userId,
    partialCompany: {},
  });

export const getPartialCompany = async (userId: any) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/registration/${userId}`);

export const updatePartialCompany = async (draft: PartialCompanyRegistration) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/registration/update`, draft);

export const getOrStartPartialRegistrationByUserId = async (userId: number) =>
  axios.post<PartialCompanyRegistration>(`${APIBASE_REVIVERUSERS}api/company/registration/${userId}`);

export const getCountries = async (langId?: number) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/get/countries?langId=${langId}`);

export const useGetCountries = (langId: number, options?: Partial<UseQueryOptions<Country[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  const language = langId || 1;
  return useQuery<Country[]>({
    queryKey: ["getCountries", language],
    queryFn: async () => {
      const result = await getCountries(language);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getRegions = async (countryIso?: string) =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/get/regions/${countryIso}`);

export const useGetRegions = (countryIso: string, options?: Partial<UseQueryOptions<Region[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<Region[]>({
    queryKey: ["getRegions", countryIso],
    queryFn: async () => {
      const result = await getRegions(countryIso);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getCompanyTypes = async () => axios.get(`${APIBASE_REVIVERUSERS}api/company/get/types`);

export const useGetCompanyTypes = (options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<CompanyType[]>({
    queryKey: ["getCompanyTypes"],
    queryFn: async () => {
      const result = await getCompanyTypes();
      return result.data.companyTypes;
    },
    ...queryOptions,
  });
};

export const getLanguages = async () => axios.get(`${APIBASE_REVIVERUSERS}api/language/all`);

export const useGetLanguages = (options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    ...options,
  };

  return useQuery<Language[] | undefined>({
    queryKey: ["getLanguages"],
    queryFn: async () => {
      const result = await getLanguages();
      return result.data.languages;
    },
    ...queryOptions,
  });
};

export const getTimeZones = async () => axios.get(`${APIBASE_REVIVERUSERS}api/company/timezone/all`);

export const useGetTimeZones = (options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<string[] | undefined>({
    queryKey: ["getTimeZones"],
    queryFn: async () => {
      const result = await getTimeZones();
      return result.data.timeZone;
    },
    ...queryOptions,
  });
};

export const getAddressTypes = async (): Promise<AxiosResponse<AddressTypes>> =>
  axios.get(`${APIBASE_REVIVERUSERS}api/company/get/address/types`);

export const useGetAddressTypes = (options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<AddressType[]>({
    queryKey: ["getAddressTypes"],
    queryFn: async () => {
      const result = await getAddressTypes();
      return result.data.addressTypes;
    },
    ...queryOptions,
  });
};

export const completePartialCompany = async (company: RegisterCompanyFormInput) =>
  axios.post(`${APIBASE_REVIVERUSERS}api/company/registration/submit`, company);

export const getMembershipTypes = async () => axios.get(`${APIBASE_REVIVERUSERS}api/company/get/membership/types`);

export const useGetMembershipTypes = (options?: Partial<UseQueryOptions<DropdownOption[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<DropdownOption[]>({
    queryKey: ["getMembershipTypes"],
    queryFn: async () => {
      const memberships = await getMembershipTypes();

      return memberships.data.map((membership: string) => ({
        value: membership,
        label: membership.toUpperCase(),
      }));
    },
    ...queryOptions,
  });
};
